import {
  FileText,
  Crosshair,
  TrendingUp,
  Layers,
  Package,
  Cog,
} from 'lucide-react';
import type React from 'react';

export type PROJECT_TYPE = 'INTERNAL' | 'EXTERNAL';
export type OPEN_FOR = 'update' | 'create' | 'delete' | '';

export type CreateProjectCardProps = {
  title?: string;
  projectType: PROJECT_TYPE;
  open: boolean;
  setOpen: (value: boolean) => void;
  project?: ProjectResponse; // default value while upate
  openFor: OPEN_FOR;
  setOpenFor: (value: OPEN_FOR) => void;
  onCreated?: (projectId: number) => Promise<void> | void;
};

type ProjectStructure = 1;

export type CreateInternalProjectRequest = {
  name: string;
  project_duration: number;
  project_structure: ProjectStructure;
  project_type: null;
};

export interface AssignedSchool {
  id: number;
  business_area: string[];
  profile_setup: boolean;
  name: string;
  created_on: string; // YYYY-MM-DD
  slogan: string | null;
  email: string | null;
  about_us: string | null;
  website: string;
  address: string;
  logo_url: string;
  primary_color: string | null;
  tenant_type: 'school' | string;
  tenant_id: number;
  country: string | null;
  telephone: string;
}
export interface ProjectResource {
  id?: number;
  name?: string;
  url?: string;
  type?: string;
}

export interface ProjectResponse {
  id: number;
  project_id?: number;
  school_id: number[];
  assigned_projects: number[] | null;
  project_mentor: string | null;
  project_status: string | number;
  project_skill_level: string[];
  number_of_docs: number;
  number_of_enrollments: number;
  number_of_forums: number;
  name: string;
  project_type: 1 | 2;
  project_structure: 1 | 2;

  methodology: string | null;
  scope_of_work: string | null;
  expected_outcomes: string | null;
  objectives: string | null;
  expected_deliverables: string | null;

  start_date: string | null;
  end_date: string | null;
  description: string | null;

  required_team_members: number | null;
  terms_and_conditions: string | null;

  created_on: string;
  updated_on: string;

  assigned_schools: AssignedSchool[] | AssignedSchool;

  assign_to_multiple_schools: boolean;

  sdgs: (number | string)[] | null;
  status: number | string; // internal: number, external: "pending"

  project_duration: number;
  project_duration_type: number;

  project_banner: string | null;
  published: boolean;

  created_by: string;
  board: any | null;

  expected_skills: {
    id?: number;
    skill?: number;
    skill_id?: number;
    skill_name: string;
    required_level: number;
    name?: string;
  }[];
  skill_framework: {
    id: number;
    name: string;
  }[];
  global_skill_framework: {
    id: number;
    name: string;
  }[];
  project_management_project?: any[];
  project_details?: any[];
  project_resorces_id?: number;
  resources: ProjectResourcesDocs[];
  members?: any[];
  tasks_completed_percentage?: number;
  docs_project?: any[];
  public_project_candidates?: ProjectCandidate[];
  publiclyAssigned?: boolean;
  project_ref?: {
    id: number;
    [key: string]: any;
  };
  evaluation_required?: unknown;
}

export interface ProjectCandidate {
  id: number;
  role: number;
  status: number;
  candidate: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    photo_url: string | null;
    profile: {
      id: number;
      photo_url: string;
      thumbnail: string;
      title: string;
      about_me: string;
    };
  };
  project: number;
}

export interface AssignedInternalProject {
  id: number; // assignment id
  project_details: ProjectResponse;
}

export interface NewProjectDuuplicateResponse {
  id: number;
  new_project: ProjectResponse;
}
export type SortKey =
  | 'newest'
  | 'oldest'
  | 'name_az'
  | 'name_za'
  | 'duration_high'
  | 'duration_low';

export interface ProjectsListPanelProps {
  title?: string;
  projects?: ProjectResponse[];
  isLoading?: boolean;
  isFetching?: boolean;
  error?: unknown;
  onRetry?: () => Promise<{ data?: unknown; error?: unknown }>;

  createLabel?: string;
  onCreate?: () => void;
  projectType: PROJECT_TYPE;

  className?: string;
  assignedInternalProjects?: boolean;
  assignedExternalProjects?: boolean;
  underlinedTabs?: React.ReactNode;
  onFilterChange?: (value: 'all' | 'assigned') => void;
}

export interface ShowProjectsProps {
  projects?: ProjectResponse[];
  projectType: PROJECT_TYPE;
  assignedInternalProjects: boolean;
}

export type MenuAction<_> =
  | {
    type?: 'item';
    key: string;
    label: string;
    icon?: React.ReactNode;
    destructive?: boolean;
    disabled?: boolean;
    hidden?: boolean;
    onSelect: (ctx: { project: ProjectResponse }) => void;
  }
  | {
    type: 'separator';
    key: string;
    hidden?: boolean;
  };

export type ProjectMenuProps<_> = {
  project: ProjectResponse;
  items: ProjectMenuItem[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  disabled?: boolean;
};

// External Project
export interface ExternalProjectApiResponse {
  id: number;
  expected_skills: string[];
  assigned_schools: AssignedSchool[];
  resources: {
    id: number;
    description: string | null;
    created_on: string;
    updated_on: string;
    path: string;
    size: string;
    name: string;
    type: string;
    project: number;
  }[];

  sdgs: string[] | null;
  project_status: number;
  name: string;
  status: string; // "pending", "approved", etc.

  methodology: string | null;
  scope_of_work: string | null;
  expected_outcomes: string | null;
  objectives: string | null;
  expected_deliverables: string | null;

  start_date: string | null;
  end_date: string | null;
  project_duration: number;
  project_duration_type: number;

  created_on: string;
  updated_on: string;
  description: string | null;
  required_team_members: number | null;
  terms_and_conditions: string | null;

  assign_to_multiple_schools: boolean;
  draft: boolean;
  project_banner: string | null;
  project_type: 2;
  project_structure: 1 | 2;
  published: boolean;
  created_by: string;

  scope: string[];
  skill_framework: string[];
  global_skill_framework: number[];
}

export interface PubliclyAssignedProjectApiResponse {
  id: number;
  project_ref?: {
    id?: number;
    project_id?: number;
    [key: string]: any;
  };
  public_project_candidates?: ProjectCandidate[];
  assigned_to?: { id?: string } | null;
  [key: string]: any;
}

// Project type flag
export type ProjectType = 'INTERNAL' | 'EXTERNAL';

// Action handlers that the menu config expects
export interface ProjectActionHandlers {
  onEdit?(project: ProjectResponse): void;
  onChangeThumbnail?(project: ProjectResponse): void;
  onDuplicate?(project: ProjectResponse): void;
  onAssignMentor?(project: ProjectResponse): void;
  onDelete?(project: ProjectResponse): void;
  onMoveToBoard?(project: ProjectResponse): void;
}

// Single menu item
export type ProjectMenuItem<T = ProjectResponse> = {
  key: string;
  label?: string;
  icon?: React.ReactNode;
  hidden?: boolean;
  disabled?: boolean;
  destructive?: boolean;
  type?: 'item' | 'separator';
  onSelect?: (ctx: { project: T }) => void;
};

export interface InternalHeaderProps {
  project: ProjectResponse;
  internalProjectId?: number;
}

export interface ShowInternalProjectLayoutProps {
  projectDetails: ProjectResponse;
  detailedId?: number;
  passedProjectId?: number;
  routeProjectId?: number;
  isAssignedView?: boolean;
  isCompany?: boolean;
  isReadOnly?: boolean;
}

export interface ProjectDetailsProps {
  project: ProjectResponse;
  isExternal?: boolean;
}

export interface AssignMentorProps {
  open: boolean;
  projectName?: string;
  clientsLoading: boolean;
  onClose: () => void;
  onSelect: (mentorId: string, mentorName: string) => void;
  currentUserId?: string;
}

export type FieldKey =
  | 'description'
  | 'objectives'
  | 'expected_outcomes'
  | 'scope_of_work'
  | 'expected_deliverables'
  | 'methodology';

export const FIELD_ICONS: Record<FieldKey, React.ElementType> = {
  description: FileText,
  objectives: Crosshair,
  expected_outcomes: TrendingUp,
  scope_of_work: Layers,
  expected_deliverables: Package,
  methodology: Cog,
};

export const FIELDS: Array<{
  label: string;
  field: FieldKey;
  placeholder: string;
  description: string;
}> = [
    {
      label: 'Description',
      field: 'description',
      placeholder: 'Add a project description...',
      description: 'Overview of what the project is about',
    },
    {
      label: 'Objectives',
      field: 'objectives',
      placeholder: 'Define project objectives...',
      description: 'Key goals and targets to achieve',
    },
    {
      label: 'Methodology',
      field: 'methodology',
      placeholder: 'Describe the methodology...',
      description: 'Approach and methods to be used',
    },
    {
      label: 'Expected Outcomes',
      field: 'expected_outcomes',
      placeholder: 'List expected outcomes...',
      description: 'Results anticipated from the project',
    },
    {
      label: 'Scope of Work',
      field: 'scope_of_work',
      placeholder: 'Define the scope of work...',
      description: 'Boundaries and extent of the project',
    },
    {
      label: 'Expected Deliverables',
      field: 'expected_deliverables',
      placeholder: 'List deliverables...',
      description: 'Tangible outputs to be produced',
    },
  ];

export const initBool = (): Record<FieldKey, boolean> => ({
  description: false,
  objectives: false,
  expected_outcomes: false,
  scope_of_work: false,
  expected_deliverables: false,
  methodology: false,
});

export const initCollapsed = (): Record<FieldKey, boolean> => ({
  description: true,
  objectives: true,
  expected_outcomes: true,
  scope_of_work: true,
  expected_deliverables: true,
  methodology: true,
});

export const buildDraft = (p: ProjectResponse): Record<FieldKey, string> => ({
  description: p.description ?? '',
  objectives: p.objectives ?? '',
  expected_outcomes: p.expected_outcomes ?? '',
  scope_of_work: p.scope_of_work ?? '',
  expected_deliverables: p.expected_deliverables ?? '',
  methodology: p.methodology ?? '',
});

export const shallowEqualDraft = (
  a: Record<FieldKey, string>,
  b: Record<FieldKey, string>
) =>
  a.description === b.description &&
  a.objectives === b.objectives &&
  a.expected_outcomes === b.expected_outcomes &&
  a.scope_of_work === b.scope_of_work &&
  a.expected_deliverables === b.expected_deliverables &&
  a.methodology === b.methodology;

export interface ProjectResourcesDocs {
  id: number;
  description: string | null;
  created_on: string;
  updated_on: string;
  path: string;
  size: string;
  name: string;
  type: string;
  project: number;
}

export type ProjectResourceResponse = ProjectResourcesDocs[];

export interface ProjectResourcesProps {
  project: ProjectResponse;
  isReadOnly?: boolean;
}
